import dayjs from 'dayjs'
import { COLOR_OPTIONS } from '../ServiceEditorPanel'
import { isDateClosed } from '../../utils/appointmentUtils'
import { getServicePrice } from '../../utils/appointmentUtils'

/**
 * calShared — helpers for the desktop calendar redesign (Claude Design
 * "Calendar · five views"). Icons, service color tints, status derivation,
 * multi-pet visit merging (group_id), and overlap/cluster placement math.
 */

// px per hour in the time grids (design: 84px/hour → 42px per 30-min slot)
export const CAL_HOUR_H = 84
export const CAL_GRID_HOURS = 24
export const CAL_WORK_START = 8   // working hours (existing app constants)
export const CAL_WORK_END = 18

/**
 * Closed ranges (in minutes-of-day) to hatch on the calendar for one day,
 * derived from the merchant's weekly hours + recurring closed dates. Display
 * only — booking on hatched time stays allowed.
 *
 * Returns { closed, ranges }: `closed` marks a fully closed day (recurring
 * closed date, or a weekday with no open intervals); `ranges` is the
 * complement of the day's open intervals. Falls back to the legacy 8–18
 * constants when the merchant has no configured weekly hours.
 */
export function calClosedRanges(day, merchant) {
	const DAY_MIN = 24 * 60
	const closedAll = { closed: true, ranges: [{ from: 0, to: DAY_MIN }] }
	const hours = merchant?.weekly_hours
	if (!hours || typeof hours !== 'object') {
		return {
			closed: false,
			ranges: [
				{ from: 0, to: CAL_WORK_START * 60 },
				{ from: CAL_WORK_END * 60, to: DAY_MIN },
			],
		}
	}

	if (isDateClosed(day, merchant?.closed_recurring_dates)) return closedAll

	const toMins = (t) => {
		const [h, m] = String(t || '').split(':').map(Number)
		return (h || 0) * 60 + (m || 0)
	}
	const intervals = (hours[String(day.day())] || [])
		.map(iv => ({ from: toMins(iv.start), to: toMins(iv.end) }))
		.filter(iv => iv.to > iv.from)
		.sort((a, b) => a.from - b.from)
	if (intervals.length === 0) return closedAll

	const ranges = []
	let cursor = 0
	intervals.forEach(iv => {
		if (iv.from > cursor) ranges.push({ from: cursor, to: iv.from })
		cursor = Math.max(cursor, iv.to)
	})
	if (cursor < DAY_MIN) ranges.push({ from: cursor, to: DAY_MIN })
	return { closed: false, ranges }
}

// ── icons (design CalIc glyphs) ─────────────────────────────────────
const CAL_IC_PATHS = {
	chevL: <path d="M10 3.5L5.5 8l4.5 4.5" />,
	chevR: <path d="M6 3.5L10.5 8L6 12.5" />,
	chevD: <path d="M3.5 6L8 10.5L12.5 6" />,
	plus: <path d="M8 3v10M3 8h10" />,
	clock: (
		<>
			<circle cx="8" cy="8" r="6" />
			<path d="M8 4.8V8l2.2 1.6" />
		</>
	),
	check: <path d="M3.5 8.5l3 3L12.5 5" />,
	user: (
		<>
			<circle cx="8" cy="5.4" r="2.6" />
			<path d="M3.2 13.5c0-2.4 2.1-4.1 4.8-4.1s4.8 1.7 4.8 4.1" />
		</>
	),
	paw: (
		<>
			<circle cx="5.2" cy="4.6" r="1.25" />
			<circle cx="10.8" cy="4.6" r="1.25" />
			<circle cx="3.2" cy="8" r="1.15" />
			<circle cx="12.8" cy="8" r="1.15" />
			<path d="M8 7.2c1.75 0 3.3 1.5 3.3 3.1 0 1.2-.8 2-1.9 2-.55 0-.95-.2-1.4-.2s-.85.2-1.4.2c-1.1 0-1.9-.8-1.9-2 0-1.6 1.55-3.1 3.3-3.1z" />
		</>
	),
	alert: (
		<>
			<path d="M8 2.2L14.4 13H1.6z" />
			<path d="M8 6.6v3" />
			<circle cx="8" cy="11.4" r=".7" fill="currentColor" stroke="none" />
		</>
	),
	ban: (
		<>
			<circle cx="8" cy="8" r="5.6" />
			<path d="M4.1 4.1l7.8 7.8" />
		</>
	),
}

export function CalIc({ name, size = 16, sw = 1.6 }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth={sw}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ flex: 'none', display: 'block' }}
		>
			{CAL_IC_PATHS[name] || null}
		</svg>
	)
}

// ── service color tints ─────────────────────────────────────────────
// Each service carries one appointment_color (COLOR_OPTIONS value). The
// design wants a family per color: accent (bar/dot), soft (card fill ~12%),
// deep (time/price text). Derive the tints from the accent hex.
const mixHex = (hex, other, amount) => {
	const h = hex.replace('#', '')
	const o = other.replace('#', '')
	const c = (i) => Math.round(
		parseInt(h.substring(i, i + 2), 16) * amount +
		parseInt(o.substring(i, i + 2), 16) * (1 - amount)
	)
	return `rgb(${c(0)}, ${c(2)}, ${c(4)})`
}

const DEFAULT_ACCENT = '#7C6BD9'
const colorSetCache = {}

export function serviceColorSet(appointmentColor) {
	const key = appointmentColor || 'default'
	if (colorSetCache[key]) return colorSetCache[key]
	const accent = COLOR_OPTIONS.find(opt => opt.value === appointmentColor && opt.value !== '')?.color || DEFAULT_ACCENT
	const set = {
		accent,
		soft: mixHex(accent, '#FFFFFF', 0.13),
		deep: mixHex(accent, '#241F38', 0.62),
	}
	colorSetCache[key] = set
	return set
}

// ── status (design: up / in / done / noshow, quiet until it matters) ──
export const CAL_STATUS = {
	up: { label: 'Upcoming', bg: '#E7F1FB', fg: '#2C6CB0' },
	pending: { label: 'Pending', bg: '#FDF3E4', fg: '#8F6217' },
	in: { label: 'In progress', bg: '#E9F6EE', fg: '#25714A' },
	done: { label: 'Completed', bg: '#F0EEF6', fg: '#6E6688' },
	noshow: { label: 'No-show', bg: '#FBECEB', fg: '#A53B36' },
}

export function calStatusOf(visit, now) {
	if (visit.status === 'noShow') return 'noshow'
	if (visit.status === 'completed') return 'done'
	if (visit.status === 'bookPending') return 'pending'
	if (now.isAfter(visit.start) && now.isBefore(visit.end)) return 'in'
	return 'up'
}

// ── time formatting (design calFmtT / calRangeT) ─────────────────────
// Minutes are always shown, including on the hour ("1:00", never "1") so
// times line up in a column and read consistently.
export function calFmtT(d, withMeridiem = true) {
	return withMeridiem ? d.format('h:mm A') : d.format('h:mm')
}

// The start drops its meridiem only when both ends share one ("9:00 – 10:30 AM").
export function calRangeT(start, end) {
	const sameAP = start.format('A') === end.format('A')
	return `${calFmtT(start, !sameAP)} – ${calFmtT(end, true)}`
}

// ── visits: merge group_id rows into one multi-pet visit block ────────
// A multi-pet booking is stored as sequential appointment rows sharing a
// group_id. The design shows the whole visit as ONE card with a segmented
// edge (a stripe per pet's service). `groupScope` extends the merge key so
// day-lane views only merge rows within the same employee lane.
const rowPrice = (row) => {
	const svc = getServicePrice(row)
	const adds = Array.isArray(row.add_ons) ? row.add_ons.reduce((s, a) => s + (a.price || 0), 0) : 0
	return svc + adds
}

// Normalize a row's add_ons jsonb into what the card / peek render:
// the card shows a "+N" count chip, the peek lists each name and price.
const rowAddOns = (row, fmtPrice) =>
	(Array.isArray(row.add_ons) ? row.add_ons : [])
		.filter(a => a && a.name)
		.map(a => ({
			name: a.name,
			price: a.price || 0,
			priceLabel: a.price ? fmtPrice(a.price) : null,
		}))

export function buildVisits(appointments, tz, { groupByEmployee = false, formatPrice = null } = {}) {
	const fmtPrice = formatPrice || ((cents) => `$${(cents / 100).toFixed(2)}`)
	const groups = new Map()
	const singles = []
	appointments.forEach(row => {
		const gid = row.group_id
		if (!gid) { singles.push([row]); return }
		const key = groupByEmployee ? `${gid}::${row.employee_id || ''}` : gid
		if (!groups.has(key)) groups.set(key, [])
		groups.get(key).push(row)
	})

	const toVisit = (rows) => {
		const sorted = [...rows].sort((a, b) => dayjs(a.start_ts).diff(dayjs(b.start_ts)))
		const first = sorted[0]
		const start = dayjs(first.start_ts).tz(tz)
		const end = sorted.reduce((max, r) => {
			const e = dayjs(r.end_ts).tz(tz)
			return e.isAfter(max) ? e : max
		}, dayjs(first.end_ts).tz(tz))
		const multi = sorted.length > 1
		const pets = sorted.map(r => {
			const rs = dayjs(r.start_ts).tz(tz)
			const re = dayjs(r.end_ts).tz(tz)
			return {
				pet: r.pets?.name || 'Pet',
				breed: r.pets?.breed || null,
				svc: r.service_types?.name || 'Service',
				mins: Math.max(1, re.diff(rs, 'minute')),
				// price = service + add-ons (card totals); svcPrice = service only
				// (the peek lists add-ons on their own priced lines).
				price: rowPrice(r),
				priceLabel: fmtPrice(rowPrice(r)),
				svcPrice: getServicePrice(r),
				svcPriceLabel: fmtPrice(getServicePrice(r)),
				adds: rowAddOns(r, fmtPrice),
				colorSet: serviceColorSet(r.service_types?.appointment_color),
			}
		})
		const total = pets.reduce((s, p) => s + p.price, 0)
		return {
			key: multi ? `g-${first.group_id}-${first.id}` : `a-${first.id}`,
			rows: sorted,
			appointment: first,
			start,
			end,
			sm: start.hour() * 60 + start.minute(),
			mins: Math.max(1, end.diff(start, 'minute')),
			multi,
			pets,
			pet: multi ? pets.map(p => p.pet).join(' & ') : pets[0].pet,
			breed: multi ? null : pets[0].breed,
			adds: multi ? [] : pets[0].adds,
			client: first.customerName || first.customer_name || 'Customer',
			svcName: multi ? `${pets.length} pets · one visit` : pets[0].svc,
			price: total,
			priceLabel: fmtPrice(total),
			colorSet: pets[0].colorSet,
			employee: first.employees
				? `${first.employees.first_name || ''} ${first.employees.last_name || ''}`.trim()
				: null,
			status: first.status,
		}
	}

	return [...singles, ...groups.values()]
		.map(toVisit)
		.sort((a, b) => a.sm - b.sm || b.mins - a.mins)
}

// ── overlap placement (design calPlacedClean: split ≥64px, else cluster) ──
// Returns [{ kind: 'appt', visit, style, z } | { kind: 'cluster', items, start, end, style, z }]
function calClusters(visits) {
	const clusters = []
	let cur = null
	let end = -1
	visits.forEach(v => {
		if (v.sm >= end) {
			cur = []
			clusters.push(cur)
			end = v.sm + v.mins
		} else {
			end = Math.max(end, v.sm + v.mins)
		}
		cur.push(v)
	})
	return clusters
}

export function placeClean(visits, colW) {
	const out = []
	const yOf = (m) => (m / 60) * CAL_HOUR_H + 1
	calClusters(visits).forEach(cl => {
		const laneEnds = []
		const withLanes = cl.map(v => {
			let lane = laneEnds.findIndex(e => e <= v.sm)
			if (lane === -1) { lane = laneEnds.length; laneEnds.push(v.sm + v.mins) }
			else laneEnds[lane] = v.sm + v.mins
			return { v, lane }
		})
		const lanes = laneEnds.length
		const splitW = (colW - 7) / lanes
		if (lanes === 1 || splitW >= 64) {
			withLanes.forEach(({ v, lane }) => out.push({
				kind: 'appt',
				visit: v,
				z: 5 + lane,
				style: {
					top: yOf(v.sm),
					height: Math.max(16, (v.mins / 60) * CAL_HOUR_H - 3),
					left: lanes === 1 ? 3 : 3 + lane * splitW,
					width: lanes === 1 ? colW - 7 : splitW - 3,
				},
			}))
		} else {
			const sm = Math.min(...cl.map(v => v.sm))
			const em = Math.max(...cl.map(v => v.sm + v.mins))
			out.push({
				kind: 'cluster',
				items: cl,
				start: cl.reduce((min, v) => (v.start.isBefore(min) ? v.start : min), cl[0].start),
				end: cl.reduce((max, v) => (v.end.isAfter(max) ? v.end : max), cl[0].end),
				z: 6,
				style: {
					top: yOf(sm),
					height: Math.max(16, ((em - sm) / 60) * CAL_HOUR_H - 3),
					left: 3,
					width: colW - 7,
				},
			})
		}
	})
	return out
}

export const calInitials = (name) =>
	(name || '')
		.split(' ')
		.filter(Boolean)
		.map(w => w[0])
		.slice(0, 2)
		.join('')
		.toUpperCase()
